// Image fetching via CORS-friendly APIs (no proxy needed).
//
// Primary: Wikipedia REST API (https://en.wikipedia.org/api/rest_v1/page/summary/<title>)
//   - CORS-enabled, no API key, returns direct stable image URLs from Wikimedia.
//   - Each anime theme maps to a canonical Wikipedia article title.
//
// Fallback: Openverse API (https://api.openverse.org/v1/images/?q=...)
//   - Free, CORS-enabled, aggregates openly-licensed images.
//
// We previously tried Bing Image Search via free CORS proxies (allorigins /
// corsproxy.io / thingproxy) but all of those proxies are now dead, 403, or
// timing out, and Bing's HTML format changed. Wikipedia + Openverse are
// reliable and don't need a proxy.

const WIKI_REST = 'https://en.wikipedia.org/api/rest_v1/page/summary/';
const WIKI_PAGEIMAGES = 'https://en.wikipedia.org/w/api.php?action=query&format=json&prop=pageimages&titles=';
const OPENVERSE = 'https://api.openverse.org/v1/images/';

export type ImageSearchResult = {
    url: string;
    source: 'wikipedia' | 'openverse';
    title?: string;
};

/** Fetch the lead image for a Wikipedia article by exact title. */
const fetchWikiImage = async (title: string): Promise<string | null> => {
    const enc = encodeURIComponent(title);

    // 1. Try REST summary endpoint (returns originalimage or thumbnail).
    try {
        const res = await fetch(`${WIKI_REST}${enc}`, {
            headers: { Accept: 'application/json' },
        });
        if (res.ok) {
            const data = await res.json();
            if (data.originalimage?.source) return data.originalimage.source;
            if (data.thumbnail?.source) return data.thumbnail.source;
        }
    } catch {
        // fall through to pageimages API
    }

    // 2. Fallback: action=query with pageimages prop (also CORS-enabled via origin=*).
    try {
        const url = `${WIKI_PAGEIMAGES}${enc}&pithumbsize=800&origin=*`;
        const res = await fetch(url);
        if (res.ok) {
            const data = await res.json();
            const pages = data.query?.pages || {};
            const firstPage = Object.values(pages)[0] as { thumbnail?: { source: string } } | undefined;
            if (firstPage?.thumbnail?.source) return firstPage.thumbnail.source;
        }
    } catch {
        // fall through to Openverse
    }

    return null;
};

/** Search Openverse for openly-licensed images matching a query. */
const fetchOpenverseImages = async (query: string, count = 5): Promise<string[]> => {
    const url = `${OPENVERSE}?q=${encodeURIComponent(query)}&page_size=${count}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Openverse HTTP ${res.status}`);
    const data = await res.json();
    return (data.results || [])
        .map((r: { url?: string }) => r.url)
        .filter((u: string | undefined): u is string => Boolean(u));
};

/**
 * Fetch a single usable image URL for a query. Tries Wikipedia first
 * (expects an exact article title), then falls back to Openverse search.
 */
export const fetchFirstUsableImage = async (query: string): Promise<string | null> => {
    // If the query looks like a Wikipedia article title (no spaces, or a
    // known mapping), try Wikipedia first.
    const wikiImage = await fetchWikiImage(query);
    if (wikiImage) return wikiImage;

    const urls = await fetchOpenverseImages(query, 3);
    return urls[0] || null;
};

/**
 * Search for candidate image URLs for a query. Returns multiple candidates
 * so the UI can offer a "next image" option.
 */
export const searchImages = async (query: string, count = 8): Promise<string[]> => {
    const candidates: string[] = [];

    // 1. Wikipedia lead image (1 candidate, high quality).
    const wiki = await fetchWikiImage(query);
    if (wiki) candidates.push(wiki);

    // 2. Openverse search (multiple candidates).
    try {
        const ov = await fetchOpenverseImages(query, count);
        candidates.push(...ov);
    } catch {
        // Openverse optional; Wikipedia alone may suffice.
    }

    if (candidates.length === 0) throw new Error(`No images found for: ${query}`);
    return candidates;
};

/** Mapping of anime theme ids to canonical Wikipedia article titles. */
export const ANIME_WIKI_TITLES: Record<string, string> = {
    'anime-dragonball-z': 'Super Saiyan Blue Goku',
    'anime-buu': 'Majin Buu',
    'anime-conan': 'Case Closed',
    'anime-beyblade-x': 'Beyblade',
    'anime-tsubasa': 'Captain Tsubasa',
    'anime-kuroko': "Kuroko's Basketball",
    'anime-slam-dunk': 'Slam Dunk (manga)',
    'anime-pokemon': 'Pikachu',
    'anime-naruto': 'Naruto',
    'anime-onepiece': 'Monkey D. Luffy',
    'anime-demonslayer': 'Tanjiro Kamado',
    'anime-mha': 'My Hero Academia',
    'anime-doraemon': 'Doraemon',
    'anime-shinchan': 'Crayon Shin-chan',
    'anime-jujutsu': 'Satoru Gojo',
    'anime-spyfamily': 'Anya Forger',
    'anime-haikyuu': 'Haikyu',
    'anime-cars': 'Lightning McQueen',
    'anime-marvel-spiderman': 'Spider-Man',
};

/** Fetch multiple images from a list of Wikipedia article titles. */
export const fetchMultipleImages = async (titles: string[]): Promise<string[]> => {
    const results: string[] = [];
    for (const title of titles) {
        const url = await fetchWikiImage(title);
        if (url) results.push(url);
    }
    return results;
};
